// The possible-Ongoing-matches WORKSPACE (busy-my-web#317, screenshot 2 of Pouria's 2026-09-03
// report).
//
// The offers are the same ones they have always been — Suggested Contains→Timeline edges, read from
// `ongoing/suggestions` — and the two answers are still the store's `addSuggestedThread` /
// `rejectSuggestedThread`. What changes here is WHERE they are asked and HOW MANY questions a
// person is asked at once:
//
//   • until #317 they were asked one row per EMAIL, in two places: the Ongoing story's "Connect this
//     email to this Ongoing" block (busy-my-web#291, now removed) and the inline unfold under the
//     Briefing's count row (busy-my-web#316, now removed);
//   • from #317 they are asked one CARD per match — "HMRC · 4 emails · updated 11 Mar", the Ongoing
//     it may belong to as a chip, and View emails / Not related / Connect — in the Email Workspace's
//     middle, reached by pressing the Briefing's "N possible Ongoing matches" row.
//
// So this module's whole job is the GROUPING: several offers about the same sender and the same
// Ongoing are ONE question ("do these belong there?"), which is what the design draws and what the
// issue asks for ("if the workspace cards group multiple emails, Connect applies to the whole
// group"). Answering a card answers every edge behind it.
//
// Pure — no store, no api, nothing mounted.

import { RELATIONS_ROUTE, connectionOffers, relationOffers } from '@/lib/emailRelationOffers'
import { emailWorkspaceOwnsMiddle, lastUpdate } from '@/lib/emailSession'
import { senderDisplayName } from '@/lib/senderDisplay'

export const TEXT = {
  // The card's grey label beside the chip — what the card IS, said once per card.
  possibleMatch: 'Possible match',
  // The three answers, in the frame's order.
  viewEmails: 'View emails',
  notRelated: 'Not related',
  connect: 'Connect',
  // The empty state. It is reachable: answer the last card and the list is empty for the moment
  // before the crumb is closed, and a blank middle would read as a fault.
  none: 'Nothing is waiting to be connected right now.',
  // Under an OPEN EMAIL the card is headed by the Ongoing (busy-my-web#621); this is its headline
  // when the offer named no title and the store does not know the story yet — never "undefined".
  untitledOngoing: 'An Ongoing',
  // …and the first word of that card's meta line: what the headline IS.
  ongoing: 'Ongoing',
}

/**
 * The name on the card: the PERSON, not the address.
 *
 * `from` arrives as an RFC header ("Aviva <policies@aviva.com>"), and the frame's cards are headed
 * "HMRC", "HSBC" — the display name where the mail carries one, the address where it does not.
 *
 * Since busy-my-web#361 the parse itself lives in lib/senderDisplay.js — the card, the mailbox
 * listing, the Briefing rows and the workspace's two listings all name a sender the same way, and
 * the copy that used to live here could only drift from them. The card's own behaviour is
 * unchanged for a single address; a header naming SEVERAL is now read by its first rather than
 * printed whole, which is the one line a card has room for anyway.
 */
export function senderName(from) {
  return senderDisplayName(from)
}

/**
 * WHICH card an offer belongs to.
 *
 * One card per (sender, Ongoing) pair: the question the card asks is "do these emails from HMRC
 * belong to Health Issues?", and two Ongoings wanting the same sender are two different questions.
 * An offer with no sender at all is its own card, keyed by its edge — the payload `ongoing/
 * suggestions` answers with today carries no `from` on thread rows (see the report for #317), so
 * that path is the one real data takes until the service adds it, and it degrades to one card per
 * email rather than to one card lumping unrelated mail together.
 */
export function matchCardKey(offer) {
  const sender = senderName(offer?.sender)
  return sender
    ? `s:${String(offer?.ongoingId || '')}|${sender.toLowerCase()}`
    : `t:${String(offer?.id || '')}`
}

/** "4 emails · updated 11 Mar" — the card's meta line; the date half is dropped when unknown. */
export function matchCardMeta(count, updated) {
  const n = Number(count) || 0
  const emails = `${n} email${n === 1 ? '' : 's'}`
  const when = String(updated || '').trim()
  return when ? `${emails} · updated ${when}` : emails
}

/**
 * The cards, from the store's suggestion list.
 *
 * The offers themselves are lib/emailRelationOffers.js's reading of the same list the count row is
 * built from — one reading, so the row and the cards can never disagree about what is pending. The
 * order is the server's ranking, taken from the FIRST offer of each card: the ranking is by
 * confidence and recency, and re-sorting a ranked list here would be this module inventing an
 * opinion it has no data for.
 */
export function matchCards(suggestions, now = new Date(), readOffers = relationOffers, keyOf = matchCardKey) {
  // `readOffers` is WHICH offers are grouped: the mail ones by default (every caller that is about
  // email), or `connectionOffers` — threads and entities — for the story's connections table
  // (`ongoingConnectionCards` below; Pouria, 2026-10-01, frame 40011046:161537).
  const byKey = new Map()
  for (const offer of readOffers(suggestions)) {
    // `keyOf` is HOW they are grouped: by (sender, Ongoing) everywhere the question is about mail,
    // by Ongoing alone under an open email (`ongoingCardKey`, busy-my-web#621).
    const key = keyOf(offer)
    const card = byKey.get(key)
    if (card) card.offers.push(offer)
    else byKey.set(key, { key, offers: [offer] })
  }
  return [...byKey.values()].map(({ key, offers }) => {
    const head = offers[0]
    const name = senderName(head.sender) || head.subject || head.story || 'This email'
    // The newest thread in the card — what "updated" states, and what "View emails" opens where the
    // card cannot be resolved to a whole sender group.
    const newest = offers.reduce((a, b) => (dateOf(b.received) > dateOf(a.received) ? b : a), head)
    // Only the offers that ARE mail have a thread behind them (an entity offer's `timelineId` is the
    // task's or note's own timeline — lib/emailRelationOffers leaves its `threadRoute` null), so
    // "View emails" and the story's `?email=` reader are only ever pointed at a conversation.
    const mail = offers.filter(o => o.threadRoute)
    const newestMail = mail.includes(newest) ? newest : mail[0] || null
    return {
      id: key,
      name,
      sender: senderName(head.sender),
      count: offers.length,
      meta: matchCardMeta(offers.length, lastUpdate(newest.received, now)),
      updated: lastUpdate(newest.received, now),
      // The chip: the Ongoing this mail may belong to. The frame colours it per CATEGORY (blue
      // "Health Issues", green "Programming"); the offer payload carries no category for a thread
      // row — see the #317 report's data gaps — and since busy-my-web#320 the chip wears the
      // SUGGESTION grey (lib/ongoingMembership's `SUGGESTED_COLOUR`) rather than the box's indigo:
      // a colour on an email means "connected to an Ongoing" everywhere else in the email views, and
      // a card is a question about a connection that does not exist yet.
      category: head.story,
      ongoingId: head.ongoingId,
      viewRoute: head.viewRoute,
      threadRoute: newestMail ? newestMail.threadRoute : null,
      // …and WHICH thread that route is, by id (busy-my-web#322): "View emails" on a card with no
      // sender group behind it opens that conversation in the reader, and the reader is asked for a
      // thread, not for a URL. The newest one, so the id and the route can never name two mails.
      threadId: (newestMail && newestMail.timelineId) || '',
      timelineIds: mail.map(o => o.timelineId).filter(Boolean),
      // The newest offer's date, raw — the connections table sorts newest first on it (Pouria,
      // 2026-10-01) and formats it in the frame's own "Sep 7" rather than `updated`'s "7 Sept".
      received: newest.received || null,
      // What the card IS (lib/possibleConnections' `connectionModule` reads it off the head offer).
      module: head.module || 'email',
      relationshipIds: offers.map(o => o.relationshipId),
      offers,
    }
  })
}

const dateOf = (value) => {
  const t = value ? new Date(value).getTime() : NaN
  return Number.isNaN(t) ? 0 : t
}

/** How many questions are pending — the count the Briefing row states and the crumb repeats. */
export function matchCount(suggestions) {
  return matchCards(suggestions).length
}

/**
 * The same cards, narrowed to ONE Ongoing (busy-my-web#333).
 *
 * The Email Workspace asks the mailbox's question — "which of these emails belong SOMEWHERE?" — and
 * draws every card. Inside an Ongoing the question is the other half of the same one: "which emails
 * may belong HERE?", so the card list is the mailbox's filtered to the story being read. One
 * grouping, two readings of it, which is why this is a filter over `matchCards` rather than a second
 * pass over the offers: the workspace's count and the story's can never be built from different
 * rules, and a card answered in one place is the same card in the other.
 *
 * No id, no cards — a briefing with no story behind it must not inherit the whole mailbox's
 * questions.
 */
export function ongoingMatchCards(suggestions, ongoingId, now = new Date()) {
  const id = String(ongoingId || '').trim()
  if (!id) return []
  return matchCards(suggestions, now).filter(c => String(c.ongoingId || '') === id)
}

/** How many questions THIS Ongoing has pending — the count its briefing row states. */
export function ongoingMatchCount(suggestions, ongoingId) {
  return ongoingMatchCards(suggestions, ongoingId).length
}

// ── THE OPEN EMAIL'S OWN MATCHES (busy-my-web#612, Saffet 2026-10-09) ─────────────────────────
//
// Saffet, with the OpenAI "One workspace name starting October 15" email open: the box said "2
// possible ongoing matches", pressing it drew FIVE cards in the middle — three "Saffet Mustafa"
// groups, IFTTT, Pinterest, none of them about the OpenAI mail — and the box listed four. Three
// numbers for one thing, because three readings were in play:
//
//   • the open email's row counted the RAW edges whose `timelineId` is the thread (2),
//   • the row opened the MAILBOX's workspace, which draws `matchCards` over every offer (5),
//   • and the box's list under the crumb showed four of those five (a 4-row scroll window, #496).
//
// So an open email now asks the mailbox's question narrowed to ITSELF: the offers whose thread is
// the open one, through the SAME grouping every other surface uses. The row counts these cards, the
// middle draws these cards, and the box lists these cards. There is no per-thread relations
// endpoint on the BFF; the thread's edges are already in `ongoing/suggestions` (each carries its
// `timelineId`), so the narrowing is a filter over the list the store polls, not a new request.

// ── …HEADED BY THE ONGOING (busy-my-web#621, Saffet 2026-10-09) ──────────────────────────────
//
// Saffet, the same OpenAI email open: "1 possible Ongoing match. When I clicked it it matched me to
// the same email as an ongoing." The one card read "Saffet Mustafa · 1 email · updated 8 Oct" with
// "Supplements" only as a chip — a SENDER group's card, which is right for the mailbox's question
// ("which of these emails belong somewhere?", #317) and for a story's connections (#539: threads
// that may belong HERE), but under an open email the question is inverted: the email is already on
// screen, and what is being offered is an ONGOING. So the open email's cards are one per Ongoing,
// headed by its title, with the email itself as the chip. ("Saffet Mustafa" was not even the
// sender: the timeline service names a thread offer by its FIRST participant, which on an inbound
// mail can be the mailbox owner — see the #621 report; the subject below is the reliable half.)

/** One card per Ongoing — the key the open email's offers are grouped by (#621). */
export function ongoingCardKey(offer) {
  const ongoingId = String(offer?.ongoingId || '').trim()
  return ongoingId ? `o:${ongoingId}` : `t:${String(offer?.id || '')}`
}

/** The Ongoing a card is about, from the store's list (`ongoing/items`) — null when not loaded. */
export function findOngoing(ongoings, ongoingId) {
  const id = String(ongoingId || '').trim()
  if (!id || !Array.isArray(ongoings)) return null
  return ongoings.find(o => o && String(o.id || '') === id) || null
}

/**
 * The Ongoing card's meta line: "Ongoing · 3 items · updated 8 Oct", each half dropped when the
 * store does not know it (the count is the story's `timelineCount` — every thread and entity it
 * holds, hence "items" rather than "emails"). Never empty: "Ongoing" alone still says what it is.
 */
export function ongoingCardMeta(ongoing, now = new Date()) {
  const n = Number(ongoing?.timelineCount) || 0
  const when = lastUpdate(ongoing?.updateDate || ongoing?.updatedDate || null, now)
  return [
    TEXT.ongoing,
    n > 0 ? `${n} item${n === 1 ? '' : 's'}` : '',
    when ? `updated ${when}` : '',
  ].filter(Boolean).join(' · ')
}

/**
 * A mailbox card re-headed for the open email it sits under (#621): the Ongoing's title as the
 * name (the store's current title first — a story renamed since the offer was made reads as it is
 * called now — then the offer's own, then a nameless phrasing), the Ongoing's meta, and the EMAIL
 * as the chip. Everything the three answers read is untouched — `relationshipIds` (Connect / Not
 * related), `sender` / `threadRoute` / `threadId` (View) — and `emailName` keeps the name the card
 * had, which is what "View emails" names the conversation it opens (`cardEmailsTarget`).
 */
export function openEmailMatchCard(card, ongoings = [], now = new Date()) {
  const ongoing = findOngoing(ongoings, card?.ongoingId)
  const title = String(ongoing?.title || '').trim() || String(card?.category || '').trim()
  const head = (card?.offers || [])[0] || {}
  return {
    ...card,
    name: title || TEXT.untitledOngoing,
    ongoingTitle: title,
    meta: ongoingCardMeta(ongoing, now),
    category: String(head.subject || '').trim() || card?.sender || '',
    emailName: card?.name || '',
    scope: 'email',
  }
}

/**
 * The cards behind ONE email — that thread's offers, one card per Ongoing, headed by the Ongoing
 * (#621). `ongoings` is the store's `ongoing/items`, read for the title / count / date; without it
 * the offer's own `ongoingTitle` heads the card.
 */
export function threadMatchCards(suggestions, threadId, now = new Date(), ongoings = []) {
  const id = String(threadId || '').trim()
  if (!id) return []
  const offers = (suggestions || []).filter(x => String(x?.timelineId || '') === id)
  return matchCards(offers, now, relationOffers, ongoingCardKey).map(c => openEmailMatchCard(c, ongoings, now))
}

/** How many questions the open email has pending — what its Briefing row states (#612). */
export function threadMatchCount(suggestions, threadId) {
  return threadMatchCards(suggestions, threadId).length
}

/**
 * The cards the possible-matches workspace is about right now: the open email's when the row that
 * opened it was an email's (#612), the whole mailbox's otherwise (#317, unchanged). One answer for
 * the middle, the crumb's count and the box's list, so the three can never disagree again.
 */
export function scopedMatchCards(suggestions, threadId = '', now = new Date(), ongoings = []) {
  return String(threadId || '').trim()
    ? threadMatchCards(suggestions, threadId, now, ongoings)
    : matchCards(suggestions, now)
}

/**
 * The frame's resting height in match rows (#496, #612) — kept as the explicit cap `matchListRows`
 * still honours when one is passed. The box no longer applies it by default (#624).
 */
export const MATCH_LIST_CAP = 4

/**
 * The box's list. #612 added a "+N more" line so a hidden-scrollbar fold could not read as a
 * miscount ("5 matches" read as "4"); #624 (Saffet 2026-10-09: "Show all five not +2 more") lists
 * EVERY match instead, so by default (`cap` Infinity) `rows` is all the cards and `more` is 0. A
 * finite `cap` still gives the first `cap - 1` rows and a count of the rest.
 */
export function matchListRows(cards, cap = Infinity) {
  const list = Array.isArray(cards) ? cards : []
  if (cap === Infinity) return { rows: list, more: 0 }
  const max = Math.max(1, Number(cap) || MATCH_LIST_CAP)
  if (list.length <= max) return { rows: list, more: 0 }
  const rows = list.slice(0, max - 1)
  return { rows, more: list.length - rows.length }
}

/** "+1 more" — the last line of an explicitly capped match list (not rendered by default since #624); '' when nothing is hidden. */
export function moreMatchesLabel(more) {
  const n = Number(more) || 0
  return n > 0 ? `+${n} more` : ''
}

/**
 * The story's POSSIBLE CONNECTIONS as cards — every module, not only mail (Pouria, 2026-10-01,
 * frame 40011046:161537: "Buy Lens cleaner" ✓, "Best sun hat for travel" 🌐, "Installment payment"
 * ✉, "Sun essentials list" ✎, "Summer Review" 📅).
 *
 * `ongoingMatchCards` above stays the MAIL reading — PossibleMatchCards.vue draws it and its
 * sentence is "which emails may belong here?". The connections table and the Briefing's "N
 * Possible connections" row ask the wider question, so they read this one; both count through the
 * same grouping, so the row's number and the table's rows cannot disagree.
 */
export function ongoingConnectionCards(suggestions, ongoingId, now = new Date()) {
  const id = String(ongoingId || '').trim()
  if (!id) return []
  return matchCards(suggestions, now, connectionOffers).filter(c => String(c.ongoingId || '') === id)
}

/** How many possible connections THIS Ongoing has pending — the Briefing row's number. */
export function ongoingConnectionCount(suggestions, ongoingId) {
  return ongoingConnectionCards(suggestions, ongoingId).length
}

/**
 * What "View emails ↗" opens when the card is being read INSIDE an Ongoing (busy-my-web#333).
 *
 * Not `cardEmailsTarget`: that one answers for the Email Workspace, where the sender's group is the
 * better reading and the reader is mounted in the middle the workspace owns. A story owns its own
 * middle and has its own way of reading a mail — `/ongoing/<id>?email=<timelineId>`, the focused-email
 * mode StoryView already draws, with the story's own header as the way back. Sending the user to the
 * mailbox instead would answer "show me this email" by leaving the Ongoing they are deciding about.
 *
 * Null where the card names no conversation, so the control can simply not act.
 */
export function storyCardEmailsTarget(card, ongoingId) {
  const id = String(ongoingId || '').trim()
  const threadId = String(card?.threadId || (card?.timelineIds || [])[0] || '').trim()
  if (!id || !threadId) return null
  return {
    kind: 'story-email',
    path: `/ongoing/${id}?email=${encodeURIComponent(threadId)}`,
    id: threadId,
    subject: String(card?.name || ''),
  }
}

/**
 * What "View emails ↗" opens.
 *
 * A card that IS a sender group the workspace already knows opens that group — the same listing the
 * Briefing's "View" opens, so the emails are read where email is read. Anything else (a sender with
 * no group, or an offer with no sender at all) opens the newest THREAD behind the card, which is the
 * only honest reading of "the group's emails" when there is no group.
 */
export function cardEmailsTarget(card, groups = []) {
  if (!card) return null
  const sender = String(card.sender || '').trim().toLowerCase()
  const group = sender
    ? (groups || []).find(g => g && String(g.sender || '').trim().toLowerCase() === sender)
    : null
  if (group) return { kind: 'group', group }
  if (!card.threadRoute) return null
  // The thread's own two facts travel with the target since busy-my-web#322: opening a conversation
  // records it on the session (that is what draws the reader into the middle and what names the
  // crumb), so the caller needs the id and a subject, not only a path. `path` is unchanged and is
  // still what a caller that must navigate uses.
  return {
    kind: 'thread',
    path: card.threadRoute,
    id: card.threadId || (card.timelineIds || [])[0] || '',
    // An open email's card is headed by its Ongoing (#621); the conversation is still named by
    // what the card was called before it was re-headed, so the reader's crumb is unchanged.
    subject: card.emailName || card.name || '',
  }
}

/**
 * Where pressing the Briefing's matches row has to GO before the cards can be seen.
 *
 * The workspace opens in place wherever the email session (or the Briefing's groups) owns the middle
 * — that is the whole point of busy-my-web#99 — so from home there is nothing to navigate: the flag
 * alone turns the middle into the cards. The two places it does NOT own are its own route (the
 * router draws it there already) and an open conversation, which wins the middle by design
 * (acc-account-sync-srv#11) — so from a thread the row takes you to the workspace, with the cards
 * asked for in the URL exactly as home's old "Possible relations" line asked for them.
 */
export function matchesOpenTarget({ path = '', sessionActive = false, hasGroups = false, threadId = '' } = {}) {
  if (String(path).startsWith('/emails/workspace')) return null
  return emailWorkspaceOwnsMiddle({ path, sessionActive, hasGroups }) ? null : relationsRoute(threadId)
}

/**
 * The workspace's URL for the cards (`?relations=1`), carrying WHICH email they are about when the
 * row pressed was an open email's (busy-my-web#612, Saffet 2026-10-09): the store's scope does not
 * survive the navigation from a thread's own page, so the URL says it, and the workspace reads it
 * back with `relationsThreadFromQuery`. Without a thread it is exactly #317's route.
 */
export function relationsRoute(threadId = '') {
  const id = String(threadId || '').trim()
  return id ? `${RELATIONS_ROUTE}&thread=${encodeURIComponent(id)}` : RELATIONS_ROUTE
}

/** The thread a `?relations=1&thread=<id>` URL scopes the cards to — '' for the mailbox's. */
export function relationsThreadFromQuery(query = {}) {
  const raw = query?.thread
  return String((Array.isArray(raw) ? raw[0] : raw) || '').trim()
}

// ── THE CONFIDENCE CUT-OFF, AND THE EVIDENCE RULE (busy-my-web#526, Saffet 2026-09-17) ──────────
//
// Saffet, adding an email to an Ongoing on dev: "it shouldn't suggest fish or bread ongoings that
// has nothing to do with it — just a search box to search an ongoing if no connection was found."
//
// The ranking the BFF answers with (`POST v1/ongoing/suggest-from-threads` → `existing[]`, read by
// lib/ongoingMatchesPanel's `readExisting`) is a SEMANTIC one: every Ongoing the user owns gets a
// score, and the top three were offered whatever those scores were. A mailbox with four Ongoings in
// it therefore always had three "matches" — the fish and the bread — because being the best of four
// says nothing about being related to anything.
//
// So a candidate has to clear TWO bars before it is presented as a match, and this is the one place
// either bar is written:
//
//   1. its confidence is at or above MATCH_CONFIDENCE_MIN, and
//   2. it shares at least one piece of EVIDENCE with the selection — a sender, a subject term or a
//      fact that both the email and the Ongoing actually carry.
//
// Bar 2 is the one that answers Saffet: a story can score highly on topic drift alone (both are
// "about food"), and topic drift is exactly what nobody asked for. Something the two genuinely
// share is the only thing that makes "possible match" a true sentence.
//
// Everything that fails either bar is NOT dropped from the app — it is simply not a MATCH. The
// picker still lists every Ongoing the user has under the matches, unmarked, as search results
// (lib/ongoingPicker's `possibleMatchListRows`), which is the "just a search box" half of the
// report. This also supersedes `defaultMatchRows`'s stand-in rows (Pouria, 2026-09-16: "we need to
// show some default name matches"): the defaults are still shown, in the listing below, but they
// are no longer WORN as suggestions.

/**
 * The lowest confidence the BFF can report for a candidate still to be called a match.
 *
 * 0.6 is the ranking's own "more alike than not" line: `existing[].confidence` is a 0–1 score and
 * the service labels a candidate `strong` from roughly 0.75. Held as ONE exported constant because
 * every surface that presents a match — the picker's listing, the group listing's fourth column —
 * has to agree about the word "possible"; a second copy of this number is a second definition.
 */
export const MATCH_CONFIDENCE_MIN = 0.6

// Words that appear in every mailbox and therefore prove nothing. They are skipped as evidence so a
// subject term like "the" can never make an unrelated story look connected.
const EVIDENCE_STOPWORDS = new Set([
  'the', 'and', 'for', 'you', 'your', 'our', 'with', 'from', 'this', 'that', 'has', 'have',
  'com', 'net', 'org', 'www', 'mail', 'email', 'emails', 'inbox', 'sender', 'subject', 'fwd', 'res',
  'new', 'ongoing', 'ongoings', 'update', 'updates', 'noreply', 'no-reply', 'info', 'hello',
])

/**
 * A piece of evidence in the one shape two of them can be compared in: lower case, punctuation
 * flattened to spaces (@ . and - kept, because an address IS its punctuation), spaces collapsed.
 */
export function evidenceKey(value) {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9@.\-\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Do these two pieces of evidence name the same thing?
 *
 * Containment either way rather than equality, because the two lists are written by different
 * hands: the selection carries "recruiting@linkedin.com" where the candidate's reason reads "shares
 * the sender linkedin.com", and a subject TERM is by definition a fragment of a subject line.
 * Nothing shorter than three characters is compared at all — a two-letter fragment is inside almost
 * every sentence in English, which is how false matches are made.
 */
const sharesOne = (a, b) => {
  if (a.length < 3 || b.length < 3) return false
  if (EVIDENCE_STOPWORDS.has(a) || EVIDENCE_STOPWORDS.has(b)) return false
  return a === b || a.includes(b) || b.includes(a)
}

/** Every comparable piece the SELECTION carries — the emails' senders, subject terms and facts. */
export function selectionEvidence({ senders = [], subjects = [], facts = [] } = {}) {
  const out = []
  for (const value of [...(senders || []), ...(subjects || []), ...(facts || [])]) {
    const key = evidenceKey(value)
    if (key && !EVIDENCE_STOPWORDS.has(key) && !out.includes(key)) out.push(key)
  }
  return out
}

/**
 * Every comparable piece a CANDIDATE carries.
 *
 * Both shapes the ranking answers in are read: the structured `evidence` block where the service
 * sends one, and the human `reasons[]` sentences it has always sent ("Shares the sender
 * recruiting@linkedin.com"). A sentence is compared whole — `sharesOne`'s containment is what finds
 * the address inside it — so nothing has to be parsed out of prose here.
 */
export function candidateEvidence(candidate) {
  const block = candidate?.evidence || {}
  const values = [
    ...(block.senders || []), ...(block.subjects || []), ...(block.facts || []),
    ...(candidate?.reasons || []),
  ]
  const out = []
  for (const value of values) {
    const key = evidenceKey(value)
    if (key && !out.includes(key)) out.push(key)
  }
  return out
}

/** What this candidate and this selection actually share — the reason the row may be shown. */
export function sharedEvidence(candidate, selection = {}) {
  const mine = candidateEvidence(candidate)
  const theirs = selectionEvidence(selection)
  return theirs.filter(t => mine.some(m => sharesOne(m, t)))
}

/**
 * Is this candidate a MATCH — both bars cleared?
 *
 * A candidate with no confidence at all (the field is absent) fails: an unscored row is the service
 * telling us it does not know, and "we do not know" is not a suggestion.
 */
export function isRealMatch(candidate, selection = {}) {
  const score = typeof candidate?.confidence === 'number' ? candidate.confidence : null
  if (score === null || score < MATCH_CONFIDENCE_MIN) return false
  return sharedEvidence(candidate, selection).length > 0
}

/** The candidates that survive both bars, in the server's own ranking order. */
export function confidentMatches(candidates = [], selection = {}) {
  return (candidates || []).filter(c => c && isRealMatch(c, selection))
}

export default matchCards
